import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { newTemplateFilterEnum } from "../../../src/taskpane/components/enums/newTemplateFilterEnum";
import { FolderViewWrapper } from "../../../src/taskpane/components/helpers/folderViewWrapper";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { SharePointFolderContentTypeIds } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import { setIsBusySaving, setIsSavingInBackground } from "../../../src/taskpane/redux/modules/savingInBackground";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { NewDocumentTemplate } from "../../../src/taskpane/interfaces/IDocumentTemplate";
import { menuItemIconStyles } from "../../../src/taskpane/config/controlStyles";
import { ArrowUpload20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/ArrowUpload20Regular";
import { getFolderPermissionMenuState, runFolderPermissionMenuAction } from "./folderPermissionMenuState";
import { getCurrentTemplateContentType, isTemplateFilterAllowed, rootContentType } from "./templateFilterHelpers";

export function useMenuItem_Upload(
    isUploadEnalbled: boolean,
    waitFor: boolean,
    newDocumentTemplates: NewDocumentTemplate[],
    pickFiles: () => void,
    pickFolder: () => void,
    setIsCMUploadFilesPanelOpen: (isCMUploadFilesPanelOpen: boolean) => void
) {
    const languageStrings = React.useContext(LanguageContext);

    const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
    const currentDirectory = useSelector((state: RootState) =>
        state.workingDirectories.workingDirectories.length > 0
            ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1]
            : null
    );
    const isBusySaving = useSelector((state: RootState) => state.savingInBackground.isBusySaving);
    const siteInfo = useSelector((state: RootState) => state.tenantInfo.sites);
    const enableNewTemplateFilters = useSelector((state: RootState) => state.tenantInfo.enableNewTemplateFilters);
    const newTemplateDocumentSetFilters = useSelector((state: RootState) => state.tenantInfo.newTemplateDocumentSetFilters);
    const newTemplateFolderFilters = useSelector((state: RootState) => state.tenantInfo.newTemplateFolderFilters);
    const newTemplateLibraryFilters = useSelector((state: RootState) => state.tenantInfo.newTemplateLibraryFilters);
    const enforceStrictDocumentSetsOnly = useSelector((state: RootState) => state.tenantInfo.enforceStrictDocumentSetsOnly);

    const dispatch = useDispatch();

    const menuItem_Upload = React.useMemo(() => {
        const folderPermissionMenuState = getFolderPermissionMenuState({
            currentSharepointFolder,
            requiredPermissions: ["canAddListItem", "canEditListItem"]
        });

        if (isUploadEnalbled && folderPermissionMenuState.shouldRender && currentDirectory.type !== ComponentTypes.SharePointList) {
            var folderViewWrapper = new FolderViewWrapper(siteInfo, currentDirectory);
            const canViewFolder = folderViewWrapper.canViewFolder();

            const isInFolder = (currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && currentDirectory.serverRelativePath)
                || (currentDirectory?.directory?.length > 0 && currentDirectory?.parentId);

            const currentContentype = getCurrentTemplateContentType({ currentDirectory });

            let canUploadFolders = false;
            let canUploadFiles = false;

            if (enableNewTemplateFilters) {
                canUploadFiles = isTemplateFilterAllowed({
                    currentContentType: currentContentype,
                    filter: newTemplateFilterEnum.file,
                    newTemplateLibraryFilters,
                    newTemplateFolderFilters,
                    newTemplateDocumentSetFilters
                });

                canUploadFolders = isTemplateFilterAllowed({
                    currentContentType: currentContentype,
                    filter: newTemplateFilterEnum.folder,
                    newTemplateLibraryFilters,
                    newTemplateFolderFilters,
                    newTemplateDocumentSetFilters
                });
            } else {
                canUploadFiles = true;
                canUploadFolders = true;
            }

            if (enforceStrictDocumentSetsOnly
                && currentContentype === rootContentType
                && (canUploadFiles || canUploadFolders)
                && newDocumentTemplates?.some(template => template?.templateId?.startsWith(SharePointFolderContentTypeIds.documentSet))) {
                canUploadFiles = false;
                canUploadFolders = false;
            }

            if (canUploadFiles && (canViewFolder || (!canViewFolder && !isInFolder))) {
                const menuItems: IContextualMenuItem[] = [{
                    key: "uploadfiles",
                    text: languageStrings.Files,
                    onClick: () => {
                        runFolderPermissionMenuAction(folderPermissionMenuState, () => {
                            dispatch(setIsSavingInBackground(false));
                            dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: null, errorMessages: null }));
                            pickFiles();
                        });
                    }
                }];

                if (currentSharepointFolder.canCreateFolder && canUploadFolders) {
                    menuItems.push({
                        key: "uploadFolders",
                        text: languageStrings.Folder,
                        onClick: () => {
                            runFolderPermissionMenuAction(folderPermissionMenuState, () => {
                                dispatch(setIsSavingInBackground(false));
                                dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: null, errorMessages: null }));
                                pickFolder();
                            });
                        },
                    })
                }

                return {
                    key: "uploadfilesfolders",
                    disabled: folderPermissionMenuState.isDisabled || !currentDirectory?.sharePointListId || waitFor === true || isBusySaving === true,
                    text: languageStrings.Upload,
                    // iconProps: { iconName: "Upload" },
                    iconProps: {},
                    onRenderIcon: () => <ArrowUpload20Regular className={menuItemIconStyles} />,
                    subMenuProps: {
                        items: menuItems
                    }
                } as IContextualMenuItem;
            }
        }

        return null;
    }, [
        isUploadEnalbled,
        waitFor,
        newDocumentTemplates,
        currentSharepointFolder,
        isBusySaving,
        currentDirectory,
        siteInfo,
        enableNewTemplateFilters,
        newTemplateLibraryFilters,
        newTemplateFolderFilters,
        newTemplateDocumentSetFilters,
        enforceStrictDocumentSetsOnly,
        languageStrings,
        dispatch,
        pickFiles,
        pickFolder,
    ]);

    return {
        menuItem_Upload,
    };
}
